import { RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import { SelectV2 } from "../../../components/select-v2";
import { ConfirmationDialog } from "../../../components/confirmation-dialog";
import { useToast } from "../../../components/toast-container";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import api from "../../../lib/axios";
import type { BaseResponse } from "../../../lib/api-contract";
import { getApiErrorMessage } from "../../../lib/result-codes";

type Source = "all" | "event" | "survey";
interface Item {
  user_id: string;
  user_name: string;
  user_email: string;
  source_type: "event" | "survey";
  source_id: string;
  source_title: string;
  expected_points: number;
}
interface Data {
  summary?: {
    event_missing_users: number;
    event_missing_rewards: number;
    survey_missing_users: number;
    survey_missing_rewards: number;
    total_missing_users: number;
    total_missing_rewards: number;
  };
  list?: Item[];
  paging?: { count?: number; total?: number };
}
const unwrap = <T,>(value: BaseResponse<T>): T => (value.data ?? {}) as T;

export default function MissingRewardsPage() {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { run, loading } = useLatestRequest();
  const [data, setData] = useState<Data>({});
  const [source, setSource] = useState<Source>("all");
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Item | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSyncAllOpen, setIsSyncAllOpen] = useState(false);
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const size = 20;
  const load = useCallback(async () => {
    setError(null);
    try {
      const result = await run((signal) =>
        api.get<BaseResponse<Data>>("/v1/portal/sync-points/missing-rewards", {
          params: { index: page - 1, size, source },
          signal,
        }),
      );
      if (result) setData(unwrap(result.data));
    } catch (requestError: unknown) {
      setData({});
      setError(getApiErrorMessage(requestError, "Failed to load missing rewards"));
    }
  }, [page, run, source]);
  useEffect(() => {
    void load();
  }, [load]);
  const sync = async () => {
    if (!selected) return;
    setIsSyncing(true);
    try {
      const result = await api.post<
        BaseResponse<{ synced: boolean; already_synced: boolean; awarded_points: number }>
      >("/v1/portal/sync-points/missing-rewards/sync", {
        user_id: selected.user_id,
        source_type: selected.source_type,
        source_id: selected.source_id,
      });
      const response = unwrap(result.data);
      addToast({
        type: "success",
        title: t("common.success"),
        message: response.synced
          ? t("missing_rewards.sync_success", { points: response.awarded_points })
          : t("missing_rewards.already_synced"),
      });
      setSelected(null);
      await load();
    } catch (requestError: unknown) {
      addToast({
        type: "error",
        title: t("common.error"),
        message: getApiErrorMessage(requestError, t("missing_rewards.sync_failed")),
      });
    } finally {
      setIsSyncing(false);
    }
  };
  const syncAll = async () => {
    setIsSyncingAll(true);
    try {
      const result = await api.post<
        BaseResponse<{
          processed: number;
          synced: number;
          already_synced: number;
          skipped: number;
          failed: number;
        }>
      >("/v1/portal/sync-points/missing-rewards/sync-all", { source });
      addToast({
        type: "success",
        title: t("common.success"),
        message: t("missing_rewards.sync_all_result", unwrap(result.data)),
      });
      setIsSyncAllOpen(false);
      await load();
    } catch (requestError: unknown) {
      addToast({
        type: "error",
        title: t("common.error"),
        message: getApiErrorMessage(requestError, t("missing_rewards.sync_all_failed")),
      });
    } finally {
      setIsSyncingAll(false);
    }
  };
  const summary = data.summary;
  const total = data.paging?.count ?? data.paging?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / size));
  return (
    <main className="p-4 md:p-6">
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("missing_rewards.title")}
          </h1>
          <p className="mt-1 text-sm text-gray-500">{t("missing_rewards.description")}</p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsSyncAllOpen(true)}
            disabled={loading || !data.list?.length}
          >
            {t("missing_rewards.sync_all")}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => void load()}
            disabled={loading}
          >
            <RefreshCw className={loading ? "mr-2 h-4 w-4 animate-spin" : "mr-2 h-4 w-4"} />
            {t("common.refresh")}
          </Button>
        </div>
      </div>
      {summary && (
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          {[
            [t("missing_rewards.events"), summary.event_missing_rewards],
            [t("missing_rewards.surveys"), summary.survey_missing_rewards],
            [t("missing_rewards.total"), summary.total_missing_rewards],
          ].map(([label, count]) => (
            <div
              key={label as string}
              className="rounded-xl border bg-white p-4 dark:bg-gray-900"
            >
              <p className="text-sm text-gray-500">{label}</p>
              <p className="mt-1 text-2xl font-bold">{count}</p>
            </div>
          ))}
        </div>
      )}
      <SelectV2
        value={source}
        onChange={(value) => {
          setSource(value as Source);
          setPage(1);
        }}
        options={[
          { value: "all", label: t("missing_rewards.all") },
          { value: "event", label: t("missing_rewards.events") },
          { value: "survey", label: t("missing_rewards.surveys") },
        ]}
        placeholder={t("missing_rewards.source")}
        ariaLabel={t("missing_rewards.source")}
        className="mb-4 w-full sm:w-72"
      />
      {error ? (
        <p className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">{error}</p>
      ) : loading && !data.list ? (
        <p className="py-12 text-center text-gray-500">{t("common.loading")}</p>
      ) : data.list?.length ? (
        <>
          <div className="overflow-x-auto rounded-xl border">
            <table className="w-full min-w-[800px] text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-600 dark:bg-gray-800">
                <tr>
                  <th className="p-4">{t("missing_rewards.user")}</th>
                  <th className="p-4">{t("missing_rewards.source")}</th>
                  <th className="p-4">{t("missing_rewards.expected_points")}</th>
                  <th className="p-4">{t("missing_rewards.action")}</th>
                </tr>
              </thead>
              <tbody>
                {data.list.map((item) => (
                  <tr
                    key={`${item.user_id}-${item.source_type}-${item.source_id}`}
                    className="border-t"
                  >
                    <td className="p-4">
                      <b>{item.user_name}</b>
                      <br />
                      <span className="text-xs text-gray-500">{item.user_email}</span>
                    </td>
                    <td className="p-4">
                      <b>{item.source_type}</b>
                      <br />
                      <span className="text-xs text-gray-500">
                        {item.source_title || item.source_id}
                      </span>
                    </td>
                    <td className="p-4 font-semibold">
                      {item.expected_points.toLocaleString()} pts
                    </td>
                    <td className="p-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setSelected(item)}
                      >
                        {t("missing_rewards.sync")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={page === 1 || loading}
              onClick={() => setPage(page - 1)}
            >
              {t("sync_points.previous")}
            </Button>
            <span className="self-center text-sm">
              {page} / {pages}
            </span>
            <Button
              type="button"
              variant="outline"
              disabled={page >= pages || loading}
              onClick={() => setPage(page + 1)}
            >
              {t("sync_points.next")}
            </Button>
          </div>
        </>
      ) : (
        <p className="rounded-xl border p-12 text-center text-gray-500">
          {t("missing_rewards.empty")}
        </p>
      )}
      <ConfirmationDialog
        isOpen={Boolean(selected)}
        onClose={() => !isSyncing && setSelected(null)}
        onConfirm={() => void sync()}
        title={t("missing_rewards.confirm_title")}
        description={
          selected
            ? t("missing_rewards.confirm_description", {
                points: selected.expected_points,
                user: selected.user_name,
              })
            : ""
        }
        confirmText={t("missing_rewards.sync")}
        cancelText={t("common.cancel")}
        isLoading={isSyncing}
        type="warning"
      />
      <ConfirmationDialog
        isOpen={isSyncAllOpen}
        onClose={() => !isSyncingAll && setIsSyncAllOpen(false)}
        onConfirm={() => void syncAll()}
        title={t("missing_rewards.sync_all_confirm_title")}
        description={t("missing_rewards.sync_all_confirm_description", {
          source: t(`missing_rewards.${source}`),
        })}
        confirmText={t("missing_rewards.sync_all")}
        cancelText={t("common.cancel")}
        isLoading={isSyncingAll}
        type="warning"
      />
    </main>
  );
}
